import type { MenuProps as AntMenuProps } from 'antd';

import {
  Dropdown as AntDropdown,
  DropDownProps as AntDropdownProps,
  MenuItemProps,
  MenuProps,
} from 'antd';
import cx from 'classnames';
import { ReactNode, useMemo } from 'react';

import colors from '../../theme/colors';
import Icon from '../Icon';
import Space from '../Space';
import styles from './Dropdown.module.less';

export type DropdownMenuOptions = (DropdownMenuItemProps | DropdownMenuItemGroupProps)[];

export type DropdownMenuItemProps = MenuItemProps & {
  key: string;
  selected?: boolean;
  displayTitle?: boolean;
  dataTestId?: string;
  selectedIcon?: ReactNode;
  searchText?: string;
  selectedLabel?: string;
};

type DropdownMenuItemGroupProps = {
  key: string;
  title: string;
  items: DropdownMenuItemProps[];
};

type DropdownProps = Omit<AntDropdownProps, 'overlay'> & {
  options: DropdownMenuOptions;
  selectedItemKeys?: string[];
  selectedIcon?: ReactNode;
  menuProps?: MenuProps;
  header?: ReactNode;
  children: ReactNode;
};

type ItemType = NonNullable<AntMenuProps['items']>[number];

function isMenuItemGroup(val: any): val is DropdownMenuItemGroupProps {
  return Array.isArray(val?.items);
}

const createMenuItem = ({
  children,
  className,
  key,
  selectedItemKeys,
  selectedIcon,
  selected,
  dataTestId,
  displayTitle,
  title,
  ...rest
}: DropdownMenuItemProps & { selectedItemKeys?: string[] }): ItemType => ({
  ...rest,
  key,
  className: cx(styles.menuItem, className),
  title: typeof title === 'string' ? title : undefined,
  ...(dataTestId && { 'data-testid': dataTestId }),
  label: (
    <Space fullWidth>
      <div className={styles.dropdownItemContainer}>
        {children}
        {(selected || selectedItemKeys?.includes(key)) &&
          (selectedIcon ?? (
            <Icon name="TickMinor" color={colors.primary[5]} title={key} size={20} />
          ))}
      </div>
    </Space>
  ),
});

export const Dropdown = ({
  children,
  header,
  menuProps,
  options,
  selectedItemKeys,
  selectedIcon,
  ...rest
}: DropdownProps) => {
  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: ItemType[] = [];

    if (header) {
      items.push({
        key: 'header',
        label: header,
        disabled: true,
        className: styles.dropdownHeader,
      });
    }

    options.forEach((option) => {
      if (isMenuItemGroup(option)) {
        items.push({
          key: option.key,
          label: option.title,
          type: 'group',
          children: option.items.map((childOption) =>
            createMenuItem({
              ...childOption,
              selectedItemKeys,
              selectedIcon,
            }),
          ),
        });
      } else {
        items.push(createMenuItem({ ...option, selectedItemKeys, selectedIcon }));
      }
    });

    return items;
  }, [options, selectedItemKeys, selectedIcon, header]);

  return (
    <AntDropdown
      menu={{ items: menuItems, className: styles.menu, ...menuProps }}
      trigger={['click']}
      {...rest}
    >
      {children}
    </AntDropdown>
  );
};
